import React, { useState, useEffect } from 'react';
import { lumi } from '../lib/lumi';
import {Shield, Mail, Calendar, User, LogOut, AlertCircle, Users, Plus, Edit2, Copy, ExternalLink, Trash2, Upload} from 'lucide-react';
import toast from 'react-hot-toast';

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'contacts' | 'partners'>('contacts');
  const [contacts, setContacts] = useState<any[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<any>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const user = lumi.auth.user;

  useEffect(() => {
    if (activeTab === 'contacts') {
      fetchContacts();
    } else {
      fetchPartners();
    }
  }, [activeTab]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const result = await lumi.entities.contacts.list({
        sort: { createdAt: -1 },
        limit: 100
      });
      setContacts(result.list);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const result = await lumi.entities.partners.list({
        sort: { createdAt: -1 },
        limit: 100
      });
      setPartners(result.list);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await lumi.auth.signIn();
      fetchContacts();
    } catch (error) {
      console.error('Sign in failed:', error);
      toast.error('Sign in failed');
    }
  };

  const handleSignOut = async () => {
    try {
      await lumi.auth.signOut();
      setContacts([]);
      setTotal(0);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contact submission?')) {
      return;
    }
    
    try {
      await lumi.entities.contacts.delete(id);
      toast.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete contact');
    }
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSavePartner = async (formData: any) => {
    try {
      let logoUrl = formData.logoUrl || '';
      
      // Upload logo if file is selected
      if (logoFile) {
        const uploadResult = await lumi.tools.file.upload([logoFile]);
        if (uploadResult[0].fileUrl) {
          logoUrl = uploadResult[0].fileUrl;
        } else {
          toast.error('Failed to upload logo');
          return;
        }
      }

      const partnerData = {
        partnerName: formData.partnerName,
        partnerSlug: formData.partnerSlug || generateSlug(formData.partnerName),
        logoUrl,
        welcomeMessage: formData.welcomeMessage || '',
        buttonLabel: formData.buttonLabel || 'Book Virtual Consult',
        referralCode: formData.referralCode || '',
        isActive: formData.isActive ?? true,
        visits: formData.visits || 0,
        ctaClicks: formData.ctaClicks || 0,
        createdAt: formData.createdAt || new Date().toISOString(),
        creator: user?.userId || 'admin'
      };

      if (editingPartner) {
        await lumi.entities.partners.update(editingPartner._id, partnerData);
        toast.success('Partner updated successfully');
      } else {
        await lumi.entities.partners.create(partnerData);
        toast.success('Partner created successfully');
      }

      setShowPartnerModal(false);
      setEditingPartner(null);
      setLogoFile(null);
      fetchPartners();
    } catch (error) {
      console.error('Failed to save partner:', error);
      toast.error('Failed to save partner');
    }
  };

  const handleDuplicatePartner = async (partner: any) => {
    const duplicatedPartner = {
      ...partner,
      partnerName: `${partner.partnerName} (Copy)`,
      partnerSlug: `${partner.partnerSlug}-copy`,
      visits: 0,
      ctaClicks: 0,
      createdAt: new Date().toISOString(),
      creator: user?.userId || 'admin'
    };
    delete duplicatedPartner._id;

    try {
      await lumi.entities.partners.create(duplicatedPartner);
      toast.success('Partner duplicated successfully');
      fetchPartners();
    } catch (error) {
      console.error('Failed to duplicate partner:', error);
      toast.error('Failed to duplicate partner');
    }
  };

  const handleToggleActive = async (partner: any) => {
    try {
      await lumi.entities.partners.update(partner._id, {
        isActive: !partner.isActive
      });
      toast.success(`Partner ${!partner.isActive ? 'activated' : 'deactivated'}`);
      fetchPartners();
    } catch (error) {
      console.error('Failed to toggle partner status:', error);
      toast.error('Failed to update partner status');
    }
  };

  const handleDeletePartner = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) {
      return;
    }
    
    try {
      await lumi.entities.partners.delete(id);
      toast.success('Partner deleted successfully');
      fetchPartners();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete partner');
    }
  };

  const copyPartnerLink = (slug: string) => {
    const url = `${window.location.origin}/p/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  // Check if user is not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 text-center">
          <Shield className="w-16 h-16 mx-auto text-cyan-400 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-gray-300 mb-6">Sign in to access the admin dashboard</p>
          <button
            onClick={handleSignIn}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold rounded-xl transition-all shadow-lg hover:scale-105"
          >
            Sign In with Lumi
          </button>
        </div>
      </div>
    );
  }

  // Check if user is not admin
  if (user.userRole !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-orange-400 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-300 mb-6">You don't have permission to access the admin portal.</p>
          <button
            onClick={handleSignOut}
            className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all border border-white/20"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className="w-8 h-8 text-cyan-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-gray-300 text-sm">Welcome, {user.userName || user.userEmail}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all border border-white/20"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'contacts'
                ? 'bg-cyan-500 text-white shadow-lg'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            <Mail size={20} />
            Contacts
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'partners'
                ? 'bg-cyan-500 text-white shadow-lg'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            <Users size={20} />
            Partners
          </button>
        </div>

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-6 h-6 text-cyan-400" />
                  <h3 className="font-semibold text-white">Total Contacts</h3>
                </div>
                <p className="text-3xl font-bold text-white">{total}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-6 h-6 text-blue-400" />
                  <h3 className="font-semibold text-white">Role</h3>
                </div>
                <p className="text-2xl font-bold text-white">{user.userRole}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-6 h-6 text-purple-400" />
                  <h3 className="font-semibold text-white">Last Updated</h3>
                </div>
                <p className="text-lg font-semibold text-white">{new Date().toLocaleDateString()}</p>
              </div>
            </div>

            {/* Contact Submissions Table */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden">
              <div className="p-6 border-b border-white/20">
                <h2 className="text-xl font-bold text-white">Contact Form Submissions</h2>
                <p className="text-gray-300 text-sm mt-1">View and manage all contact inquiries</p>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-gray-300">Loading submissions...</p>
                </div>
              ) : contacts.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">No contact submissions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Referral Code</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Message</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {contacts.map((contact) => (
                        <tr key={contact._id} className="hover:bg-white/5 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {new Date(contact.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {contact.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            <a href={`mailto:${contact.email}`} className="hover:text-cyan-400 transition-colors">
                              {contact.email}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {contact.travelAgency || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-300 max-w-md truncate">
                            {contact.message}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleDeleteContact(contact._id)}
                              className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all border border-red-500/30"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Partner Landing Pages</h2>
                <p className="text-gray-300 text-sm mt-1">Create and manage white-label partner pages</p>
              </div>
              <button
                onClick={() => {
                  setEditingPartner(null);
                  setShowPartnerModal(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all shadow-lg"
              >
                <Plus size={20} />
                New Partner
              </button>
            </div>

            {loading ? (
              <div className="p-12 text-center bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-300">Loading partners...</p>
              </div>
            ) : partners.length === 0 ? (
              <div className="p-12 text-center bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl">
                <Users className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400 mb-4">No partners yet</p>
                <button
                  onClick={() => {
                    setEditingPartner(null);
                    setShowPartnerModal(true);
                  }}
                  className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all"
                >
                  Create Your First Partner
                </button>
              </div>
            ) : (
              <div className="grid gap-6">
                {partners.map((partner) => (
                  <div
                    key={partner._id}
                    className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4 flex-1">
                        {partner.logoUrl && (
                          <img
                            src={partner.logoUrl}
                            alt={partner.partnerName}
                            className="w-16 h-16 object-contain rounded-lg bg-white p-2"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">{partner.partnerName}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              partner.isActive
                                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                            }`}>
                              {partner.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm mb-3">/p/{partner.partnerSlug}</p>
                          {partner.welcomeMessage && (
                            <p className="text-gray-400 text-sm mb-3 line-clamp-2">{partner.welcomeMessage}</p>
                          )}
                          <div className="flex gap-6 text-sm">
                            <div>
                              <span className="text-gray-400">Visits:</span>
                              <span className="text-white font-semibold ml-2">{partner.visits || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">CTA Clicks:</span>
                              <span className="text-white font-semibold ml-2">{partner.ctaClicks || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Conversion:</span>
                              <span className="text-white font-semibold ml-2">
                                {partner.visits > 0 
                                  ? `${Math.round((partner.ctaClicks / partner.visits) * 100)}%`
                                  : '0%'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyPartnerLink(partner.partnerSlug)}
                          className="p-2 bg-white/10 hover:bg-white/20 text-cyan-400 rounded-lg transition-all"
                          title="Copy Link"
                        >
                          <Copy size={18} />
                        </button>
                        <a
                          href={`/p/${partner.partnerSlug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-white/10 hover:bg-white/20 text-blue-400 rounded-lg transition-all"
                          title="View Page"
                        >
                          <ExternalLink size={18} />
                        </a>
                        <button
                          onClick={() => {
                            setEditingPartner(partner);
                            setShowPartnerModal(true);
                          }}
                          className="p-2 bg-white/10 hover:bg-white/20 text-yellow-400 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDuplicatePartner(partner)}
                          className="p-2 bg-white/10 hover:bg-white/20 text-purple-400 rounded-lg transition-all"
                          title="Duplicate"
                        >
                          <Copy size={18} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(partner)}
                          className="p-2 bg-white/10 hover:bg-white/20 text-gray-400 rounded-lg transition-all"
                          title={partner.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {partner.isActive ? '⏸' : '▶'}
                        </button>
                        <button
                          onClick={() => handleDeletePartner(partner._id)}
                          className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Partner Modal */}
      {showPartnerModal && (
        <PartnerModal
          partner={editingPartner}
          onClose={() => {
            setShowPartnerModal(false);
            setEditingPartner(null);
            setLogoFile(null);
          }}
          onSave={handleSavePartner}
          logoFile={logoFile}
          setLogoFile={setLogoFile}
        />
      )}
    </div>
  );
};

// Partner Modal Component
const PartnerModal: React.FC<{
  partner: any;
  onClose: () => void;
  onSave: (data: any) => void;
  logoFile: File | null;
  setLogoFile: (file: File | null) => void;
}> = ({ partner, onClose, onSave, logoFile, setLogoFile }) => {
  const [formData, setFormData] = useState({
    partnerName: partner?.partnerName || '',
    partnerSlug: partner?.partnerSlug || '',
    logoUrl: partner?.logoUrl || '',
    welcomeMessage: partner?.welcomeMessage || '',
    buttonLabel: partner?.buttonLabel || 'Book Virtual Consult',
    referralCode: partner?.referralCode || '',
    isActive: partner?.isActive ?? true,
    visits: partner?.visits || 0,
    ctaClicks: partner?.ctaClicks || 0,
    createdAt: partner?.createdAt || new Date().toISOString(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.partnerName) {
      toast.error('Partner name is required');
      return;
    }
    onSave(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-navy-900 border border-white/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/20">
          <h2 className="text-2xl font-bold text-white">
            {partner ? 'Edit Partner' : 'Create New Partner'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Partner Name *
            </label>
            <input
              type="text"
              value={formData.partnerName}
              onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., Pacific Travel Agency"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              URL Slug (auto-generated from name)
            </label>
            <input
              type="text"
              value={formData.partnerSlug}
              onChange={(e) => setFormData({ ...formData, partnerSlug: e.target.value })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., pacific-travel"
            />
            <p className="text-xs text-gray-400 mt-1">URL: /p/{formData.partnerSlug || 'your-slug'}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Partner Logo
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg cursor-pointer transition-all border border-white/20"
              >
                <Upload size={18} />
                Choose File
              </label>
              {logoFile && (
                <span className="text-sm text-gray-300">{logoFile.name}</span>
              )}
              {!logoFile && formData.logoUrl && (
                <img src={formData.logoUrl} alt="Current logo" className="h-12 w-12 object-contain rounded bg-white p-1" />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Welcome Message
            </label>
            <textarea
              value={formData.welcomeMessage}
              onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 h-24"
              placeholder="Welcome message for travelers..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Button Label
            </label>
            <input
              type="text"
              value={formData.buttonLabel}
              onChange={(e) => setFormData({ ...formData, buttonLabel: e.target.value })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Book Virtual Consult"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Referral Code (optional)
            </label>
            <input
              type="text"
              value={formData.referralCode}
              onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., PACIFIC2025"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-5 h-5 text-cyan-500 bg-white/10 border-white/20 rounded focus:ring-cyan-500"
            />
            <label htmlFor="isActive" className="text-sm font-semibold text-gray-300">
              Active (page is publicly accessible)
            </label>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all shadow-lg"
            >
              {partner ? 'Update Partner' : 'Create Partner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
